/**
 * Terms of Service Page
 * Algemene Voorwaarden voor SuperKonnected Platform
 * Versie 1.0 | Laatst bijgewerkt: 19 februari 2026
 * 
 * Styled with SuperKonnected brand identity:
 * - Sage green (#8BA59B)
 * - Warm beige/latte background
 * - Glassmorphism effects
 * - Inter font family
 */

import React from 'react';
import { FileText, AlertCircle, Shield, Users, Building2, Mail } from 'lucide-react';
import PageHeader from './ui/PageHeader';
import TranslateButton from './ui/TranslateButton';

// Brand colors
const colors = {
  sage: '#8BA59B',
  sageLight: 'rgba(139, 165, 155, 0.15)',
  textPrimary: 'rgba(60, 55, 50, 0.92)',
  textSecondary: 'rgba(80, 75, 70, 0.85)',
  textTertiary: 'rgba(100, 95, 70, 0.7)',
  textMuted: 'rgba(100, 95, 90, 0.6)',
  border: 'rgba(139, 165, 155, 0.2)',
  cardBg: 'rgba(248, 246, 243, 0.95)',
  sectionBg: 'rgba(248, 246, 243, 0.5)',
};

const TermsOfService: React.FC = () => {
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    height: '100vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    WebkitOverflowScrolling: 'touch',
    background: 'linear-gradient(135deg, #E8E6E1 0%, #D8D6D1 100%)',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif",
    padding: '40px 20px',
  };

  const cardStyle: React.CSSProperties = {
    background: colors.cardBg,
    backdropFilter: 'blur(12px)',
    WebkitBackdropFilter: 'blur(12px)',
    border: '1px solid rgba(0, 0, 0, 0.04)',
    borderRadius: '24px',
    padding: '32px',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.08)',
    maxWidth: '900px',
    margin: '0 auto',
  };

  const iconBoxStyle: React.CSSProperties = {
    width: '40px',
    height: '40px',
    background: colors.sageLight,
    borderRadius: '12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: `1px solid ${colors.border}`,
  };

  const headingStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '16px',
  };

  const textStyle: React.CSSProperties = {
    fontSize: '15px',
    lineHeight: '1.7',
    color: colors.textSecondary,
    marginBottom: '12px',
  };

  const highlightBoxStyle: React.CSSProperties = {
    background: colors.sectionBg,
    borderRadius: '16px',
    padding: '20px',
    marginTop: '16px',
    border: `1px solid ${colors.border}`,
  };

  const cleanMindBoxStyle: React.CSSProperties = {
    background: 'rgba(139, 165, 155, 0.1)',
    borderRadius: '16px',
    padding: '24px',
    marginTop: '16px',
    border: `2px solid ${colors.sage}`,
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '40px', paddingBottom: '32px', borderBottom: `1px solid ${colors.border}` }}>
          <div style={{ 
            display: 'inline-flex',
            alignItems: 'center', 
            justifyContent: 'center',
            width: '64px',
            height: '64px',
            background: colors.sageLight,
            borderRadius: '50%',
            marginBottom: '20px'
          }}>
            <FileText style={{ width: '32px', height: '32px', color: colors.sage }} />
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: 700, color: colors.textPrimary, marginBottom: '8px' }}>
            Algemene Voorwaarden
          </h1>
          <p style={{ fontSize: '16px', color: colors.textSecondary, marginBottom: '4px' }}>
            Terms of Service
          </p>
          <p style={{ fontSize: '13px', color: colors.textMuted, marginBottom: '16px' }}>
            Versie 1.0 | Laatst bijgewerkt: 19 februari 2026
          </p>
          <TranslateButton />
        </div>

        {/* Introduction */}
        <div style={sectionStyle}>
          <p style={textStyle}>
            Welkom bij SuperKonnected, het eerste Clean Mind social media platform voor ondernemers en managers. 
            Door gebruik te maken van ons platform ga je akkoord met deze Algemene Voorwaarden. 
            Lees deze zorgvuldig door voordat je gebruik maakt van het platform.
          </p>
        </div>

        {/* 1. Bedrijfsgegevens */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Building2 style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>1. Bedrijfsgegevens</h2>
              <p style={textStyle}>Deze Algemene Voorwaarden zijn van toepassing op diensten aangeboden door:</p>
              <div style={highlightBoxStyle}>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Bedrijfsnaam:</strong> Business Security B.V.</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Merknaam:</strong> SuperKonnected</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>Website:</strong> <a href="https://superkonnected.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com</a></p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>E-mail:</strong> support@superkonnected.com</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>KvK-nummer:</strong> 91885582</p>
                <p style={{ ...textStyle, marginBottom: '8px' }}><strong>BTW-nummer:</strong> NL865 804 515 B01</p>
                <p style={{ ...textStyle, marginBottom: 0 }}><strong>Adres:</strong> Rontgenweg 2A, 8503 AG Joure, Nederland</p>
              </div>
            </div>
          </div>
        </div>

        {/* 2. Definities */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>2. Definities</h2>
          <p style={textStyle}>In deze Algemene Voorwaarden wordt verstaan onder:</p>
          <div style={highlightBoxStyle}>
            <p style={{ ...textStyle, marginBottom: '8px' }}>• <strong>Platform:</strong> SuperKonnected, het online social media platform toegankelijk via superkonnected.com en bijbehorende applicaties.</p>
            <p style={{ ...textStyle, marginBottom: '8px' }}>• <strong>Gebruiker:</strong> Iedere natuurlijke of rechtspersoon die een account aanmaakt of gebruik maakt van het Platform.</p>
            <p style={{ ...textStyle, marginBottom: '8px' }}>• <strong>Content:</strong> Alle informatie, berichten, foto's, video's en andere materialen die via het Platform worden gedeeld.</p>
            <p style={{ ...textStyle, marginBottom: '8px' }}>• <strong>Clean Mind:</strong> De kernfilosofie van SuperKonnected: rust, focus en professionele verbinding zonder ruis, drama of ongepaste afleiding.</p>
            <p style={{ ...textStyle, marginBottom: '8px' }}>• <strong>Account:</strong> De persoonlijke registratie van een Gebruiker op het Platform.</p>
            <p style={{ ...textStyle, marginBottom: 0 }}>• <strong>Diensten:</strong> Alle functionaliteiten en services die via het Platform worden aangeboden.</p>
          </div>
        </div>

        {/* 3. Toepasselijkheid */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>3. Toepasselijkheid</h2>
          <p style={textStyle}>3.1 Deze Algemene Voorwaarden zijn van toepassing op alle overeenkomsten tussen SuperKonnected en de Gebruiker, alsmede op elk gebruik van het Platform.</p>
          <p style={textStyle}>3.2 Door een account aan te maken of het Platform te gebruiken, ga je uitdrukkelijk akkoord met deze voorwaarden.</p>
          <p style={textStyle}>3.3 Afwijkingen van deze voorwaarden zijn alleen geldig indien schriftelijk overeengekomen met SuperKonnected.</p>
        </div>

        {/* 4. Account en Registratie */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>4. Account en Registratie</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>4.1 Registratie</p>
            <p style={textStyle}>Voor gebruik van het Platform is registratie vereist. Je kunt je registreren via e-mail of via Google OAuth. Bij registratie dien je juiste en volledige informatie te verstrekken.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>4.2 Doelgroep</p>
            <p style={textStyle}>Het Platform is uitsluitend bestemd voor ondernemers en managers van 18 jaar en ouder. Door je te registreren verklaar je dat je aan deze criteria voldoet.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>4.3 Beveiliging</p>
            <p style={textStyle}>Je bent zelf verantwoordelijk voor het vertrouwelijk houden van je inloggegevens. Meld ongeautoriseerd gebruik van je account direct via support@superkonnected.com. SuperKonnected is niet aansprakelijk voor schade die voortvloeit uit onbevoegd gebruik van je account.</p>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>4.4 Eén account per persoon</p>
            <p style={textStyle}>Het is niet toegestaan meerdere accounts aan te maken. SuperKonnected behoudt zich het recht voor duplicaataccounts te verwijderen.</p>
          </div>
        </div>

        {/* 5. Clean Mind Platformafspraken */}
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <div style={iconBoxStyle}>
              <Shield style={{ width: '20px', height: '20px', color: colors.sage }} />
            </div>
            <div style={{ flex: 1 }}>
              <h2 style={headingStyle}>5. Clean Mind Platformafspraken</h2>
              <p style={textStyle}>SuperKonnected is een Clean Mind platform. Door gebruik te maken van het Platform ga je akkoord met de volgende gedragsregels:</p>
              <div style={cleanMindBoxStyle}>
                <ul style={{ marginLeft: '20px', ...textStyle, marginBottom: 0 }}>
                  <li>Je draagt bij aan een positieve, professionele en opbouwende omgeving.</li>
                  <li>Je deelt geen negatief nieuws, roddels, blame-content of content die anderen beschadigt.</li>
                  <li>Je respecteert ieders proces, tempo en professionele grenzen.</li>
                  <li>Je plaatst geen clickbait, spam of misleidende content.</li>
                  <li>Je bent verantwoordelijk voor je eigen energie en de uitwerking van jouw content op anderen.</li>
                  <li>Je helpt mee om de gemeenschap zuiver, veilig en waardevol te houden.</li>
                </ul>
              </div>
              <p style={{ ...textStyle, marginTop: '16px', marginBottom: 0 }}>
                <strong>Overtreding</strong> van de Clean Mind afspraken kan leiden tot waarschuwing, tijdelijke schorsing of permanente verwijdering van het Platform.
              </p>
            </div>
          </div>
        </div>

        {/* 6. Verboden Gebruik */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>6. Verboden Gebruik</h2>
          <p style={textStyle}>Het is verboden om het Platform te gebruiken voor:</p>
          <ul style={{ marginLeft: '20px', ...textStyle }}>
            <li>Het plaatsen van illegale, beledigende, discriminerende, racistische of haatdragende content.</li>
            <li>Intimidatie, stalking of pesten van andere gebruikers.</li>
            <li>Het verspreiden van misleidende informatie of valse identiteiten.</li>
            <li>Inbreuk op intellectueel eigendom van derden.</li>
            <li>Commercieel gebruik zonder toestemming van SuperKonnected (waaronder ongewenste reclame of spam).</li>
            <li>Het omzeilen van technische beveiligingsmaatregelen van het Platform.</li>
            <li>Activiteiten die de werking of beschikbaarheid van het Platform kunnen schaden.</li>
          </ul>
        </div>

        {/* 7. Content en Intellectueel Eigendom */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>7. Content en Intellectueel Eigendom</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>7.1 Jouw Content</p>
            <p style={textStyle}>Je behoudt alle intellectuele eigendomsrechten op de content die je op het Platform plaatst. Door content te plaatsen verleen je SuperKonnected een niet-exclusieve, royaltyvrije, wereldwijde licentie om deze content te tonen, te verspreiden en te verwerken voor de uitvoering van de Diensten.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>7.2 Platform Content</p>
            <p style={textStyle}>Alle content van SuperKonnected, waaronder het logo, design, teksten, software en de Clean Mind filosofie, is het exclusieve eigendom van SuperKonnected en is beschermd door auteursrecht en andere intellectuele eigendomsrechten. Het is niet toegestaan deze content te kopiëren of te gebruiken zonder schriftelijke toestemming.</p>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>7.3 Copyright Meldingen</p>
            <p style={textStyle}>Indien je meent dat content op het Platform inbreuk maakt op jouw auteursrechten, kun je dit melden via support@superkonnected.com. SuperKonnected zal dergelijke meldingen onderzoeken en indien gegrond de betreffende content verwijderen.</p>
          </div>
        </div>

        {/* 8. OAuth en Externe Diensten */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>8. OAuth en Externe Diensten</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.1 Google OAuth</p>
            <p style={textStyle}>Voor inloggen maakt SuperKonnected gebruik van Google OAuth. Door in te loggen met Google ga je akkoord met het delen van de volgende gegevens met SuperKonnected: naam, e-mailadres en profielfoto. SuperKonnected gebruikt deze gegevens uitsluitend voor accountbeheer en communicatie.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.2 LinkedIn Integratie</p>
            <p style={textStyle}>Als je kiest voor LinkedIn integratie, geef je SuperKonnected toestemming om geselecteerde LinkedIn-profielgegevens te importeren. Deze gegevens worden uitsluitend gebruikt voor de doeleinden die je expliciet hebt goedgekeurd bij het verlenen van toegang.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.3 Facebook Integratie</p>
            <p style={textStyle}>Als je kiest voor Facebook integratie, geef je SuperKonnected toestemming om geselecteerde Facebook-profielgegevens te importeren en/of content te delen via jouw Facebook-account. Deze gegevens worden uitsluitend gebruikt voor de doeleinden die je expliciet hebt goedgekeurd.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.4 Instagram Integratie</p>
            <p style={textStyle}>Als je kiest voor Instagram integratie, geef je SuperKonnected toestemming om geselecteerde Instagram-profielgegevens te importeren en/of content te delen via jouw Instagram-account. Deze gegevens worden uitsluitend gebruikt voor de doeleinden die je expliciet hebt goedgekeurd.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.5 X / Twitter Integratie</p>
            <p style={textStyle}>Als je kiest voor X (voorheen Twitter) integratie, geef je SuperKonnected toestemming om geselecteerde X-profielgegevens te importeren en/of content te delen via jouw X-account. Deze gegevens worden uitsluitend gebruikt voor de doeleinden die je expliciet hebt goedgekeurd.</p>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>8.6 Geen Verkoop van Data</p>
            <p style={textStyle}>SuperKonnected verkoopt jouw persoonsgegevens nooit aan derden. Gegevens worden uitsluitend gedeeld met derden voor zover noodzakelijk voor de uitvoering van de Diensten, conform onze Privacy Policy.</p>
          </div>
        </div>

        {/* 9. Privacy */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>9. Privacy en Gegevensbescherming</h2>
          <p style={textStyle}>
            SuperKonnected verwerkt persoonsgegevens conform de Algemene Verordening Gegevensbescherming (AVG/GDPR). 
            Voor volledige informatie over hoe wij omgaan met jouw persoonsgegevens verwijzen wij naar onze Privacy Policy, 
            beschikbaar op: <a href="https://superkonnected.com/privacy" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com/privacy</a>
          </p>
          <p style={textStyle}>
            Je hebt te allen tijde het recht op inzage, correctie, verwijdering en overdraagbaarheid van jouw persoonsgegevens. 
            Verzoeken hiertoe kun je indienen via support@superkonnected.com.
          </p>
        </div>

        {/* 10. Cookies */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>10. Cookies en Tracking</h2>
          <p style={textStyle}>
            SuperKonnected maakt gebruik van functionele cookies die noodzakelijk zijn voor het functioneren van het Platform. 
            Voor analytische of marketingcookies wordt altijd voorafgaande toestemming gevraagd. Voor meer informatie verwijzen wij 
            naar ons Cookiebeleid, beschikbaar op <a href="https://superkonnected.com/cookies" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com/cookies</a>.
          </p>
        </div>

        {/* 11. Betalingen */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>11. Betalingen en Abonnementen</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>11.1 Gratis en Betaalde Versies</p>
            <p style={textStyle}>SuperKonnected biedt zowel een gratis als betaalde versies aan. De exacte functionaliteiten en prijzen per versie worden duidelijk gecommuniceerd op het Platform en kunnen van tijd tot tijd worden aangepast.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>11.2 Betalingen</p>
            <p style={textStyle}>Betalingen worden verwerkt via veilige, gecertificeerde externe payment providers. Alle prijzen zijn inclusief BTW, tenzij anders vermeld. SuperKonnected is gerechtigd prijzen te wijzigen, waarbij bestaande abonnees minimaal 30 dagen van tevoren worden geïnformeerd.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>11.3 Herroepingsrecht</p>
            <p style={textStyle}>Consumenten hebben conform de Wet Koop op Afstand een herroepingstermijn van 14 dagen na aankoop, tenzij de dienst al volledig is uitgevoerd met uitdrukkelijke toestemming vóór het verstrijken van de herroepingstermijn.</p>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>11.4 Opzegging</p>
            <p style={textStyle}>Je kunt je abonnement op elk moment opzeggen via de accountinstellingen. Opzegging gaat in aan het einde van de lopende abonnementsperiode. Er wordt geen restitutie verleend voor reeds betaalde periodes, tenzij wettelijk vereist.</p>
          </div>
        </div>

        {/* 12. Beschikbaarheid */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>12. Beschikbaarheid en Onderhoud</h2>
          <p style={textStyle}>
            SuperKonnected streeft naar een optimale beschikbaarheid van het Platform, maar kan geen ononderbroken of foutloze werking garanderen. 
            Het Platform kan tijdelijk niet beschikbaar zijn vanwege gepland onderhoud of onvoorziene omstandigheden. SuperKonnected zal bij geplande 
            uitval de Gebruikers tijdig informeren.
          </p>
        </div>

        {/* 13. Aansprakelijkheid */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>13. Aansprakelijkheid</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>13.1 Beperking Aansprakelijkheid</p>
            <p style={textStyle}>De aansprakelijkheid van SuperKonnected is beperkt tot directe schade en bedraagt nooit meer dan het door de Gebruiker in de drie maanden voorafgaand aan de schadeveroorzakende gebeurtenis betaalde abonnementsbedrag.</p>
          </div>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>13.2 Uitsluitingen</p>
            <p style={textStyle}>SuperKonnected is niet aansprakelijk voor:</p>
            <ul style={{ marginLeft: '20px', ...textStyle }}>
              <li>Indirecte schade, gevolgschade of gederfde winst.</li>
              <li>Schade als gevolg van content geplaatst door andere gebruikers.</li>
              <li>Schade als gevolg van tijdelijke onbeschikbaarheid van het Platform.</li>
              <li>Schade als gevolg van overmacht (zie artikel 14).</li>
            </ul>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>13.3 Content Verantwoordelijkheid</p>
            <p style={textStyle}>Gebruikers zijn zelf verantwoordelijk voor de content die zij plaatsen. SuperKonnected is niet aansprakelijk voor schade veroorzaakt door content van Gebruikers.</p>
          </div>
        </div>

        {/* 14. Overmacht */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>14. Overmacht</h2>
          <p style={textStyle}>
            SuperKonnected is niet gehouden tot nakoming van enige verplichting indien dit wordt verhinderd als gevolg van overmacht. 
            Onder overmacht wordt verstaan: technische storingen bij derden, DDoS-aanvallen, internetstoringen, overheidsmaatregelen, 
            pandemieën, natuurrampen of andere omstandigheden buiten de redelijke controle van SuperKonnected.
          </p>
        </div>

        {/* 15. Account Opzegging */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>15. Account Opzegging en Beëindiging</h2>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ ...textStyle, fontWeight: 600 }}>15.1 Door de Gebruiker</p>
            <p style={textStyle}>Je kunt je account op elk moment verwijderen via de accountinstellingen. Na verwijdering wordt je data verwerkt conform ons data retentiebeleid zoals beschreven in de Privacy Policy.</p>
          </div>
          <div>
            <p style={{ ...textStyle, fontWeight: 600 }}>15.2 Door SuperKonnected</p>
            <p style={textStyle}>SuperKonnected behoudt zich het recht voor om accounts te schorsen of te verwijderen bij:</p>
            <ul style={{ marginLeft: '20px', ...textStyle }}>
              <li>Overtreding van deze Algemene Voorwaarden.</li>
              <li>Overtreding van de Clean Mind platformafspraken.</li>
              <li>Frauduleuze of illegale activiteiten.</li>
              <li>Langdurige inactiviteit (meer dan 24 maanden).</li>
            </ul>
            <p style={textStyle}>Bij schorsing of verwijdering door SuperKonnected wegens verwijtbaar gedrag, bestaat geen recht op restitutie van betaalde abonnementsgelden.</p>
          </div>
        </div>

        {/* 16. Wijzigingen */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>16. Wijzigingen in de Voorwaarden</h2>
          <p style={textStyle}>
            SuperKonnected behoudt zich het recht voor deze Algemene Voorwaarden te allen tijde te wijzigen. Belangrijke wijzigingen worden 
            minimaal 30 dagen van tevoren gecommuniceerd via e-mail of via een melding op het Platform. Voortgezet gebruik van het Platform 
            na de ingangsdatum van gewijzigde voorwaarden geldt als acceptatie van de nieuwe voorwaarden.
          </p>
        </div>

        {/* 17. Toepasselijk Recht */}
        <div style={sectionStyle}>
          <h2 style={headingStyle}>17. Toepasselijk Recht en Geschillenbeslechting</h2>
          <p style={textStyle}>
            Op deze Algemene Voorwaarden is uitsluitend Nederlands recht van toepassing. Geschillen worden bij voorkeur in onderling overleg opgelost. 
            Indien partijen er niet uitkomen, is de bevoegde rechter in het arrondissement waar SuperKonnected gevestigd is bevoegd, tenzij dwingend 
            recht een andere rechter aanwijst.
          </p>
          <p style={textStyle}>
            SuperKonnected hecht waarde aan klanttevredenheid en moedigt aan om klachten eerst te melden via support@superkonnected.com alvorens 
            juridische stappen te ondernemen.
          </p>
        </div>

        {/* 18. Contact */}
        <div style={{ ...highlightBoxStyle, marginBottom: '32px' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'start' }}>
            <Mail style={{ width: '24px', height: '24px', color: colors.sage, flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <h3 style={{ ...headingStyle, marginBottom: '12px' }}>18. Contact</h3>
              <p style={textStyle}>Voor vragen over deze Algemene Voorwaarden kun je contact opnemen via:</p>
              <div style={{ ...textStyle, fontSize: '14px' }}>
                <p>📧 E-mail: support@superkonnected.com</p>
                <p>🌐 Website: <a href="https://superkonnected.com" target="_blank" rel="noopener noreferrer" style={{ color: colors.sage, textDecoration: 'underline' }}>https://superkonnected.com</a></p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ 
          paddingTop: '24px', 
          borderTop: `1px solid ${colors.border}`,
          textAlign: 'center',
          fontSize: '13px',
          color: colors.textMuted
        }}>
          <p>SuperKonnected: het eerste Clean Mind social media platform voor ondernemers.</p>
          <p style={{ marginTop: '8px' }}>© 2026 SuperKonnected. Alle rechten voorbehouden.</p>
        </div>

        {/* Back Link */}
        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button
            onClick={() => window.history.back()}
            style={{
              color: colors.sage,
              fontWeight: 500,
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '15px',
              transition: 'opacity 200ms ease'
            }}
            onMouseOver={(e) => e.currentTarget.style.opacity = '0.7'}
            onMouseOut={(e) => e.currentTarget.style.opacity = '1'}
          >
            Ga terug
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
